// SharedPresentationView.js - Read-only presentation viewer
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPresentationByShareId } from '../firestoreService';
import { renderSlideToCanvas } from '../canvas/SlideCanvasRenderer';
import { loadWebFonts } from '../canvas/fontLoader';
import './SharedPresentationView.css';

function SharedPresentationView() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  
  // Loading & error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  
  // Presentation data
  const [presentation, setPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  
  // Canvas refs - separate for main view and thumbnails
  const mainCanvasRef = useRef(null);
  const thumbnailCanvasRefs = useRef([]);
  
  // Load fonts on mount
  useEffect(() => {
    loadWebFonts().then(() => {
      setFontsLoaded(true);
    });
  }, []);
  
  // Fetch presentation by shareId
  useEffect(() => {
    const fetchPresentation = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        
        const data = await getPresentationByShareId(shareId);
        
        setPresentation(data);
        
        
      } catch (err) {
        
        setError(err.message || 'Failed to load presentation');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (shareId) {
      fetchPresentation();
    }
  }, [shareId]);
  
  // Render current slide to main canvas whenever slide changes
  useEffect(() => {
    if (!fontsLoaded || !presentation || !presentation.slides || !mainCanvasRef.current) {
      return;
    }
    
    const currentSlide = presentation.slides[currentSlideIndex];
    if (!currentSlide || !currentSlide.extractedContent) {
      return;
    }
    
    const templateId = presentation.templateIds?.[currentSlideIndex] || 'modern_bullets';
    const imageUrl = presentation.imageUrls?.[currentSlideIndex] || null;
    
    renderSlideToCanvas(
      mainCanvasRef.current,
      currentSlide.extractedContent,
      templateId,
      imageUrl
    ).catch(err => {
     
    });
    
  }, [currentSlideIndex, presentation, fontsLoaded]);
  
  // Render all thumbnails once
  useEffect(() => {
    if (!fontsLoaded || !presentation || !presentation.slides) {
      return;
    }
    
    presentation.slides.forEach((slide, index) => {
      const canvas = thumbnailCanvasRefs.current[index];
      
      if (canvas && slide.extractedContent) {
        const templateId = presentation.templateIds?.[index] || 'modern_bullets';
        const imageUrl = presentation.imageUrls?.[index] || null;
        
        setTimeout(() => {
          renderSlideToCanvas(
            canvas,
            slide.extractedContent,
            templateId,
            imageUrl
          ).catch(err => {
           
          });
        }, 100 * index); // Stagger rendering slightly
      }
    });
  }, [presentation, fontsLoaded]);
  
  // Navigate slides
  const goToNextSlide = () => {
    if (presentation && currentSlideIndex < presentation.slides.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
  };
  
  const goToPrevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextSlide();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevSlide();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlideIndex, presentation]); // Dependencies ensure latest state
  
  // Loading state
  if (isLoading) {
    return (
      <div className="shared-view-container">
        <div className="shared-view-loading">
          <div className="loading-spinner"></div>
          <p>Loading presentation...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="shared-view-container">
        <div className="shared-view-error">
          <div className="error-icon">⚠️</div>
          <h2>Presentation not found</h2>
          <p>{error}</p>
          <button 
            className="back-home-btn"
            onClick={() => navigate('/')}
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }
  
  // No presentation
  if (!presentation || !presentation.slides || presentation.slides.length === 0) {
    return (
      <div className="shared-view-container">
        <div className="shared-view-error">
          <div className="error-icon">📭</div>
          <h2>No slides found</h2>
          <p>This presentation doesn't contain any slides.</p>
          <button 
            className="back-home-btn"
            onClick={() => navigate('/')}
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="shared-view-container">
      {/* Header with CTA */}
      <header className="shared-view-header">
        <div className="header-left">
          <img src="/logo-s.png" alt="Cayoty" className="header-logo" />
          <span className="header-title">Cayoty</span>
        </div>
        
        <div className="header-center">
          <h1 className="presentation-title">{presentation.title || 'Untitled Presentation'}</h1>
          <p className="creator-info">Created by {presentation.creatorName || 'Anonymous'}</p>
        </div>
        
        <div className="header-right">
          <button 
            className="cta-button"
            onClick={() => window.open('/', '_blank')}
          >
            Create Your Own
          </button>
        </div>
      </header>
      
      {/* Main slide viewer */}
      <div className="shared-view-main">
        <div className="slide-viewer">
          {/* Current slide display */}
          <div className="current-slide-container">
            <canvas
              ref={mainCanvasRef}
              width={1920}
              height={1080}
              className="slide-canvas"
            />
            
            {/* Navigation arrows */}
            {currentSlideIndex > 0 && (
              <button 
                className="nav-arrow nav-arrow-left"
                onClick={goToPrevSlide}
                title="Previous slide"
                aria-label="Previous slide"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            )}
            
            {currentSlideIndex < presentation.slides.length - 1 && (
              <button 
                className="nav-arrow nav-arrow-right"
                onClick={goToNextSlide}
                title="Next slide"
                aria-label="Next slide"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            )}
            
            {/* Slide counter */}
            <div className="slide-counter">
              {currentSlideIndex + 1} / {presentation.slides.length}
            </div>
          </div>
          
          {/* Slide thumbnails */}
          <div className="slide-thumbnails">
            {presentation.slides.map((slide, index) => (
              <div
                key={index}
                className={`thumbnail-item ${index === currentSlideIndex ? 'active' : ''}`}
                onClick={() => setCurrentSlideIndex(index)}
                role="button"
                tabIndex={0}
                aria-label={`Go to slide ${index + 1}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCurrentSlideIndex(index);
                  }
                }}
              >
                <canvas
                  ref={el => {
                    thumbnailCanvasRefs.current[index] = el;
                  }}
                  width={320}
                  height={180}
                  className="thumbnail-canvas"
                />
                <span className="thumbnail-number">{index + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Footer CTA */}
      <footer className="shared-view-footer">
        <div className="footer-content">
          <p>Want to create presentations like this?</p>
          <button 
            className="footer-cta-button"
            onClick={() => window.open('/', '_blank')}
          >
            Start Creating for Free
          </button>
        </div>
      </footer>
    </div>
  );
}

export default SharedPresentationView;